import type { CSSProperties, ReactNode } from 'react';

export function formatVN(value: number, minDec = 0, maxDec = 2): string {
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: minDec,
    maximumFractionDigits: maxDec,
  }).format(value);
}

export function formatSigned(value: number, currency: string, decimals = 2): string {
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatVN(Math.abs(value), decimals, decimals)} ${currency}`;
}

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="max-w-2xl">
      {eyebrow && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
          {eyebrow}
        </span>
      )}
      <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        {title}
      </h2>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-slate-500 md:text-base">{subtitle}</p>}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl border border-slate-200/80 bg-white p-5 md:p-7 ${className}`}>
      {children}
    </div>
  );
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-semibold text-slate-700">{children}</span>
      {hint && <span className="font-mono text-xs font-medium text-slate-400">{hint}</span>}
    </div>
  );
}

export function NumberInput({
  value,
  onChange,
  suffix,
  min,
  step,
}: {
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  min?: number;
  step?: number;
}) {
  const stepValue = step ?? 1;
  const commit = (next: number) => {
    const clamped = typeof min === 'number' && next < min ? min : next;
    onChange(Number(clamped.toFixed(6)));
  };
  return (
    <div className="relative">
      <input
        type="number"
        value={value}
        min={min}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="spin-hidden w-full rounded-xl border border-slate-200 bg-white py-3 pl-4 pr-16 font-mono text-base font-semibold text-slate-800 outline-none transition tab-focus"
      />
      <span className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">
        {suffix}
      </span>
      <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 flex-col gap-0.5">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Tăng"
          onClick={() => commit(value + stepValue)}
          className="flex h-4 w-5 cursor-pointer items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <i className="ri-arrow-up-s-fill text-[15px] leading-none" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label="Giảm"
          onClick={() => commit(value - stepValue)}
          className="flex h-4 w-5 cursor-pointer items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <i className="ri-arrow-down-s-fill text-[15px] leading-none" />
        </button>
      </div>
    </div>
  );
}

export function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 font-mono text-base font-semibold text-slate-800 outline-none transition tab-focus"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
    </div>
  );
}

export function QuickChips({
  options,
  value,
  onChange,
  format,
}: {
  options: number[];
  value: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = Math.abs(o - value) < 0.001;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={`whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              active ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={active ? { backgroundColor: 'var(--tab-accent, #7c3aed)' } : undefined}
          >
            {format ? format(o) : o}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex rounded-xl bg-slate-100 p-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? '' : 'bg-slate-300'
      }`}
      style={checked ? { backgroundColor: 'var(--tab-accent, #7c3aed)' } : undefined}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export function Stat({
  label,
  value,
  accent,
  valueClassName = '',
}: {
  label: string;
  value: string;
  accent?: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3.5">
      <p className="text-[11px] font-medium text-slate-500">{label}</p>
      <p
        className={`mt-1 font-mono text-sm font-bold tabular-nums ${valueClassName}`}
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

export function Slider({
  value,
  onChange,
  min,
  max,
  step,
  accent = '#7c3aed',
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  accent?: string;
}) {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      style={
        {
          '--fill': `${percent}%`,
          '--slider-accent': accent,
          '--slider-track': '#e2e8f0',
        } as CSSProperties
      }
    />
  );
}